import type { ReactNode } from "react";
import {
  ClerkProvider,
  ClerkLoading,
  ClerkFailed,
  SignedIn,
  SignedOut,
  SignIn,
  useAuth,
} from "@clerk/clerk-react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { ConvexRoot, convexClient } from "./convex";
import { ClerkCompanyProvider, DevCompanyProvider } from "./company";
import { clerkPublishableKey, isClerkEnabled } from "./clerkEnv";

/**
 * Root providers. With a Clerk key: Clerk auth + Convex-with-Clerk + a company
 * provider. If no Clerk organization is active, the mock site uses a shared
 * demo company. Without a Clerk key: the dev
 * path (plain Convex + the localStorage company switcher).
 */
export function AppProviders({ children }: { children: ReactNode }) {
  if (isClerkEnabled && convexClient !== null) {
    return (
      <ClerkProvider publishableKey={clerkPublishableKey}>
        <ClerkLoading>
          <Centered><p role="status">Loading secure sign-in…</p><p className="mt-3 text-sm">If sign-in does not load on a preview domain, use <a className="underline" href="https://lawstream.sitemuster.com">the shared staging site</a>.</p></Centered>
        </ClerkLoading>
        <ClerkFailed>
          <Centered><p role="alert">Sign-in is unavailable on this domain. Authentication has not been bypassed.</p><a className="mt-3 underline" href="https://lawstream.sitemuster.com">Open the shared staging site</a></Centered>
        </ClerkFailed>
        <ConvexProviderWithClerk client={convexClient} useAuth={useAuth}>
          <SignedIn>
            <ClerkCompanyProvider>{children}</ClerkCompanyProvider>
          </SignedIn>
          <SignedOut>
            <Centered>
              <SignIn routing="hash" />
            </Centered>
          </SignedOut>
        </ConvexProviderWithClerk>
      </ClerkProvider>
    );
  }

  // Dev mode — no auth.
  return (
    <ConvexRoot>
      <DevCompanyProvider>{children}</DevCompanyProvider>
    </ConvexRoot>
  );
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--paper)] p-6">
      {children}
    </div>
  );
}
